import React, { useState, useEffect } from "react";
import axiosInstance from "../../services/auth";
import "./Logistics.css";
import { toast } from "react-toastify";

const PurchaseForm = ({
  selectedCompany,
  companyId,
  companyCode,
  purchase, // If editing an existing purchase, its data is passed here.
  onClose,
}) => {
  // This state holds the stocks data fetched from the stocks API.
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stockSearches, setStockSearches] = useState({});
  const [showStockDropdown, setShowStockDropdown] = useState({});

  // Fetch stocks data (for stock selection) from the API.
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await axiosInstance.get(`/logistics/stock/${companyId}`);
        // Assuming response.data.data is the array of stocks.
        setStocks(response.data.stocks || []);
      } catch (error) {
        toast.error("Error fetching stocks data");
      }
    };
    fetchStocks();
  }, [companyId]);

  // Initialize form state.
  // In edit mode, we map each purchase item so that each item has a "stock" property that is the stock _id.
  const initialFormData = purchase
    ? {
        ...purchase,
        invoiceDate: purchase.invoiceDate
          ? new Date(purchase.invoiceDate).toISOString().split("T")[0]
          : "",
        // Map each purchase item.
        items: purchase.items && purchase.items.length > 0
          ? purchase.items.map((item) => ({
              // If item.stock is an object, extract its _id; otherwise, assume it's already an id.
              stock: item.stock && item.stock._id ? item.stock._id : item.stock,
              item: item.item || "",
              description: item.description || "",
              quantity: item.quantity || "",
              unitPrice: item.unitPrice || "",
              amount: item.amount || 0,
            }))
          : [],
      }
    : {
        company: companyId,
        invoiceNo: "",
        shopName: "",
        invoiceDate: "",
        remarks: "",
        items: [
          {
            stock: "",
            item: "",
            description: "",
            quantity: "",
            unitPrice: "",
            amount: 0,
          },
        ],
        totalAmount: 0,
      };

  const [formData, setFormData] = useState(initialFormData);

  // Calculate total purchase amount.
  const calculateTotalAmount = (updatedItems = formData.items) => {
    const total = updatedItems.reduce(
      (acc, item) => acc + parseFloat(item.amount || 0),
      0
    );
    setFormData((prev) => ({ ...prev, totalAmount: total }));
  };

  // Generic change handler.
  // For fields inside items, the expected name format is "items.index.field"
  const handleChange = (e, index) => {
    const { name, value } = e.target;
    if (name.startsWith("items")) {
      const [_, idx, field] = name.split(".");
      const updatedItems = [...formData.items];
      updatedItems[parseInt(idx)] = {
        ...updatedItems[parseInt(idx)],
        [field]: value,
      };
      // If quantity or unitPrice changes, update amount.
      if (field === "quantity" || field === "unitPrice") {
        const quantity = parseFloat(updatedItems[parseInt(idx)].quantity || 0);
        const unitPrice = parseFloat(updatedItems[parseInt(idx)].unitPrice || 0);
        updatedItems[parseInt(idx)].amount = quantity * unitPrice;
      }
      setFormData({ ...formData, items: updatedItems });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle dynamic stock item changes.
  const handleStockItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...formData, items: updatedItems });
  };

  // Add a new purchase item row.
  const handleAddItemRow = () => {
    const newRow = {
      stock: "",
      item: "",
      description: "",
      quantity: "",
      unitPrice: "",
      amount: 0,
    };
    setFormData({ ...formData, items: [...formData.items, newRow] });
  };

  // Remove a purchase item row.
  const handleRemoveItemRow = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  // Recalculate totals when items change.
  useEffect(() => {
    calculateTotalAmount(formData.items);
  }, [formData.items]);

  // Close modal when clicking outside.
  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.invoiceNo || !formData.shopName || !formData.invoiceDate) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      if (purchase) {
        // Update existing purchase
        await axiosInstance.patch(`/logistics/purchase/${purchase._id}`, formData);
        toast.success("Purchase updated successfully");
      } else {
        // Create new purchase
        await axiosInstance.post("/logistics/purchase", formData);
        toast.success("Purchase created successfully");
      }
      onClose();
    } catch (error) {
      // console.error("Error saving purchase:", error);
      toast.error("Error saving purchase");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      onClick={handleOutsideClick}
      style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content invoice_model">
          <div className="modal-header text-white text-center vesel_form">
            <h5 className="modal-title">
              {purchase ? "Edit Purchase" : "Create Purchase"}
            </h5>
            <button type="button" className="btn-close text-white" onClick={onClose}></button>
          </div>
          <div className="modal-body vesel_form">
            <form onSubmit={handleSubmit}>
              {/* Purchase Details Section */}
              <div className="card shadow-sm mb-4 rounded-3">
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">Purchase Details</h5>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Invoice No</label>
                      <input
                        type="text"
                        className="form-control"
                        name="invoiceNo"
                        value={formData.invoiceNo}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Shop Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="shopName"
                        value={formData.shopName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Invoice Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="invoiceDate"
                        value={formData.invoiceDate}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Remarks</label>
                      <textarea
                        className="form-control"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Purchase Items Section */}
              <div className="card shadow-sm mb-4 rounded-3">
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">Purchase Items</h5>
                  {formData.items &&
                    Array.isArray(formData.items) &&
                    formData.items.map((item, index) => (
                      <div key={index} className="row mb-3">
                        <div className="col-md-4 mb-3 position-relative">
                          <label className="form-label">Stock Selection</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type stock number or name"
                            value={
                              stockSearches[index] ||
                              (item.stock
                                ? (() => {
                                    const s = stocks.find(
                                      (st) => st._id === item.stock,
                                    );
                                    return s
                                      ? `${s.stockNo} - ${s.name}`
                                      : "";
                                  })()
                                : "")
                            }
                            onChange={(e) => {
                              setStockSearches((prev) => ({
                                ...prev,
                                [index]: e.target.value,
                              }));
                              handleStockItemChange(index, "stock", "");
                            }}
                            onFocus={() => {
                              setShowStockDropdown((prev) => {
                                const next = {};
                                next[index] = true;
                                return next;
                              });
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowStockDropdown((prev) => ({
                                  ...prev,
                                  [index]: false,
                                }));
                              }, 150);
                            }}
                            required
                          />
                          {showStockDropdown[index] && (
                            <ul
                              className="list-group position-absolute"
                              style={{
                                zIndex: 1200,
                                width: "100%",
                                maxHeight: 200,
                                overflowY: "auto",
                              }}
                            >
                              {stocks
                                .filter(
                                  (stock) =>
                                    (stock.name || "")
                                      .toLowerCase()
                                      .includes(
                                        (stockSearches[index] || "")
                                          .toLowerCase(),
                                      ) ||
                                    (stock.stockNo
                                      ? stock.stockNo.toString()
                                      : ""
                                    ).includes(
                                      stockSearches[index] || "",
                                    ),
                                )
                                .map((stock) => (
                                  <li
                                    key={stock._id}
                                    className="list-group-item list-group-item-action"
                                    style={{ cursor: "pointer" }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      handleStockItemChange(
                                        index,
                                        "stock",
                                        stock._id,
                                      );
                                      setStockSearches((prev) => ({
                                        ...prev,
                                        [index]: "",
                                      }));
                                      setShowStockDropdown((prev) => ({
                                        ...prev,
                                        [index]: false,
                                      }));
                                    }}
                                  >
                                    {stock.stockNo} - {stock.name}
                                  </li>
                                ))}
                              {stocks.filter(
                                (stock) =>
                                  (stock.name || "")
                                    .toLowerCase()
                                    .includes(
                                      (stockSearches[index] || "")
                                        .toLowerCase(),
                                    ) ||
                                  (stock.stockNo
                                    ? stock.stockNo.toString()
                                    : ""
                                  ).includes(
                                    stockSearches[index] || "",
                                  ),
                              ).length === 0 && (
                                <li className="list-group-item list-group-item-action">
                                  No results found
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                        <div className="col-md-2 mb-3">
                          <label className="form-label">Item Name</label>
                          <input
                            type="text"
                            className="form-control"
                            name={`items.${index}.item`}
                            value={item.item || ""}
                            onChange={(e) => handleChange(e, index)}
                            required
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-control custom-textarea"
                            name={`items.${index}.description`}
                            value={item.description || ""}
                            onChange={(e) => handleChange(e, index)}
                            rows="2"
                            required
                          />
                        </div>
                        <div className="col-md-2 mb-3">
                          <label className="form-label">Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`items.${index}.quantity`}
                            value={item.quantity === 0 ? "" : item.quantity}
                            onChange={(e) => {
                              const value = e.target.value === "" ? "" : Math.max(0, e.target.value);
                              handleChange({ target: { name: `items.${index}.quantity`, value } }, index);
                            }}
                            required
                          />
                        </div>
                        <div className="col-md-2 mb-3">
                          <label className="form-label">Unit Price</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`items.${index}.unitPrice`}
                            value={item.unitPrice === 0 ? "" : item.unitPrice}
                            onChange={(e) => {
                              const value = Math.max(0, e.target.value);
                              handleChange({ target: { name: `items.${index}.unitPrice`, value } }, index);
                            }}
                            required
                          />
                        </div>
                        <div className="col-md-2 mb-3">
                          <label className="form-label">Amount</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`items.${index}.amount`}
                            value={item.amount}
                            readOnly
                          />
                        </div>
                        <div className="col-md-1 d-flex justify-content-center align-items-center">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoveItemRow(index)}
                          >
                            <i className="bi bi-trash3"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm me-2"
                    onClick={handleAddItemRow}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    <span>Add</span>
                  </button>
                </div>
              </div>
              {/* Total Section */}
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label">Total Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    name="totalAmount"
                    value={formData.totalAmount.toFixed(2)}
                    readOnly
                  />
                </div>
              </div>
              <div className="mt-4 text-center">
                <button type="submit" className="button-2" disabled={loading}>
                  {loading
                    ? purchase
                      ? "Updating"
                      : "Creating"
                    : purchase
                    ? "Update Purchase"
                    : "Create Purchase"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseForm;
